import React, { memo, useEffect, useState } from 'react';
import clsx from 'clsx';

import styles from './styles.module.scss';

import Modal from '@components/_shared/modal';
import { BaseComponentProps } from '@utils/types/baseComponents';
import Button from '@components/_shared/button';
import TextInput from '@components/_shared/textInput';
import { RadioChoice } from '@components/_shared/radioChoice';
import ImagesDropzone from '@components/landing/_shared/imagesDropzone';
import { PostRentalProjectionFields } from '@api/landings/postRentalProjection';
import { isURL } from '@utils/helpers';
import ImageWithLoader from '@components/_shared/imageWithLoader';

type Props = {
  email: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (fields: Partial<PostRentalProjectionFields>) => void;
  submitting?: boolean;
} & BaseComponentProps;

const defaultValues: Partial<Omit<PostRentalProjectionFields, 'photos'>> & {
  photos: {
    url: string;
    file: File;
  }[];
} = {
  furnished_state: 'yes',
  is_previously_listed: false,
  external_channel_url: '',
  additional_external_channel_url: '',
  other_notes: '',
  photos: [],
};

const ContactFormModal = ({
  email,
  isOpen,
  onClose,
  onConfirm,
  submitting,
}: Props) => {
  const [reset, setReset] = useState(false);
  const [fields, setFields] = useState<
    Partial<Omit<PostRentalProjectionFields, 'photos'>> & {
      photos: {
        url: string;
        file: File;
      }[];
    }
  >(defaultValues);

  const resetForm = () => {
    setFields(defaultValues);
    setReset(true);
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
    return () => {
      setReset(false);
    };
  }, [isOpen]);

  const onFieldChange = (
    field: keyof Partial<PostRentalProjectionFields>,
    value: any
  ) => {
    setFields({ ...fields, [field]: value });
  };

  const onSubmit = () => {
    onConfirm({
      ...fields,
      photos: fields.photos.map((photo) => photo.url),
    });
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      containerStyle={styles.modal__container}
    >
      <div className={styles.modal}>
        <div className={styles.modal__title}>A few more questions</div>

        <div className={styles.modal__label}>Is the property furnished?</div>
        <div className={styles.modal__radioGroup}>
          <RadioChoice
            isSelected={fields.furnished_state === 'yes'}
            onClick={() => onFieldChange('furnished_state', 'yes')}
            theme="dark"
          >
            Yes
          </RadioChoice>
          <RadioChoice
            isSelected={fields.furnished_state === 'no'}
            onClick={() => onFieldChange('furnished_state', 'no')}
            theme="dark"
          >
            No
          </RadioChoice>
          <RadioChoice
            isSelected={fields.furnished_state === 'partially'}
            onClick={() => onFieldChange('furnished_state', 'partially')}
            theme="dark"
          >
            Partially
          </RadioChoice>
        </div>

        <div className={styles.modal__label}>
          Was the property previously listed on Airbnb or VRBO?
        </div>
        <div className={styles.modal__radioGroup}>
          <RadioChoice
            isSelected={!!fields.is_previously_listed}
            onClick={() => onFieldChange('is_previously_listed', true)}
            theme="dark"
          >
            Yes
          </RadioChoice>
          <RadioChoice
            isSelected={!fields.is_previously_listed}
            onClick={() => onFieldChange('is_previously_listed', false)}
            theme="dark"
          >
            No
          </RadioChoice>
        </div>

        <TextInput
          label="Airbnb or VRBO URL"
          placeholder="https://airbnb.com/rooms/..."
          containerStyle={clsx([
            styles.modal__input,
            fields.external_channel_url &&
              !isURL(fields.external_channel_url) &&
              styles.withError,
          ])}
          onTextChange={(value) => onFieldChange('external_channel_url', value)}
          theme="dark"
          reset={reset}
          disabled={!fields.is_previously_listed}
        />

        <TextInput
          label="Zillow or Streeteasy listing URL"
          placeholder="https://streeteasy.com/building/..."
          containerStyle={clsx([
            styles.modal__input,
            fields.additional_external_channel_url &&
              !isURL(fields.additional_external_channel_url) &&
              styles.withError,
          ])}
          onTextChange={(value) =>
            onFieldChange('additional_external_channel_url', value)
          }
          theme="dark"
          reset={reset}
        />

        <TextInput
          label="Any other notes"
          placeholder="Other notes"
          containerStyle={styles.modal__input}
          onTextChange={(value) => onFieldChange('other_notes', value)}
          theme="dark"
          reset={reset}
        />

        <div className={styles.modal__label}>Property photos</div>
        {!!fields?.photos?.length && (
          <div className={styles.modal__photos}>
            {fields.photos.map((photo, index) => (
              <ImageWithLoader
                key={`photo-${index}`}
                src={URL.createObjectURL(photo.file)}
                alt={`Property photo ${index + 1}`}
                className={styles.photoPreview}
                layout="fixed"
                width={100}
                height={80}
                sizes="100px"
              />
            ))}
          </div>
        )}

        <ImagesDropzone
          email={email}
          onImagesUploaded={(images) =>
            onFieldChange('photos', [...(fields.photos || []), ...images])
          }
        />

        <Button
          onClick={onSubmit}
          loading={submitting}
          containerStyle={styles.modal__confirm}
          variant="greenContained"
          disabled={
            (!!fields.additional_external_channel_url &&
              !isURL(fields.additional_external_channel_url)) ||
            (!!fields.external_channel_url &&
              !isURL(fields.external_channel_url))
          }
        >
          Submit
        </Button>
      </div>
    </Modal>
  );
};

export default memo(ContactFormModal);
